import {NodeSearch} from '../../components/NodeSearch'
import {
    ReactFlow,
    Panel,
    Background,
    Controls,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    getNodesBounds,
    getViewportForBounds,
    getBezierPath,
    BaseEdge,
    EdgeLabelRenderer,
    Handle,
    Node,
    Position,
    MarkerType,
} from '@xyflow/react'
import type {EdgeProps, NodeProps} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './flow.css'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {Edge} from '@xyflow/react'
import WidgetsIcon from '@mui/icons-material/Widgets'
import SmartToySharpIcon from '@mui/icons-material/SmartToySharp'
import TextFieldsSharpIcon from '@mui/icons-material/TextFieldsSharp'
import ListSharpIcon from '@mui/icons-material/ListSharp'
import ChecklistRtlSharpIcon from '@mui/icons-material/ChecklistRtlSharp'
import TableChartSharpIcon from '@mui/icons-material/TableChartSharp'
import AutoAwesomeSharpIcon from '@mui/icons-material/AutoAwesomeSharp'
import UploadFileSharpIcon from '@mui/icons-material/UploadFileSharp'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import {HistoryContext} from '../../contexts/history'
import {Dependency, QuestionFlow, QuestionNode} from '../../types/flowData'
import {v4 as uuidv4} from 'uuid'
import {toPng} from 'html-to-image'
import {FlowApi} from '../../services/FlowApi.service'
import {useNavigate} from 'react-router-dom'
const TREE_HORIZONTAL_SPACING = 420
const TREE_VERTICAL_SPACING = 220

// Interfaces atualizadas para corresponder à nova estrutura
interface QuestionOption {
    title: string
    content: string
    index: number
}

interface TableHeader {
    label: string
    name: string
    size: number
}

interface QuestionType {
    text?: {
        placeholder: string | null
        default: string | null
        allowed_answer_type: string
    }
    select?: {
        options: QuestionOption[]
        default: number
        placeholder: string
        allowed_answer_type: string
    }
    multiselect?: {
        options: QuestionOption[]
        default: number
        placeholder: string
        allowed_answer_type: string
    }
    table?: {
        headers: TableHeader[]
        items: any[]
        type: string
    }
    autonomous?: boolean
    document_upload?: {
        documents: {name: string; filepath: string}[]
    }
}

interface FlowQuestion {
    id: string
    created_at: string
    is_active: boolean
    content: string
    label: string
    instruction: string | null
    tooltip?: string | null
    assistant_id: string | null
    head: boolean
    question_type:
        | 'text'
        | 'select'
        | 'multiselect'
        | 'table'
        | 'autonomous'
        | 'radio'
        | 'checkbox'
        | 'table_autonomous'
        | 'document_upload'
    document_section: string
    type: QuestionType
    following_questions: string[]
    following_question_keys?: string[]
    depends_on: string[]
    optional?: boolean
}

interface FlowData {
    [key: string]: FlowQuestion
}

const normalizeFollowingQuestions = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string')
    }

    if (value && typeof value === 'object') {
        return Object.values(value)
            .map((item) => {
                if (typeof item === 'string') return item
                if (item && typeof item === 'object') {
                    const candidate = (item as any).question_id
                    return typeof candidate === 'string' ? candidate : null
                }
                return null
            })
            .filter((item): item is string => Boolean(item))
    }

    return []
}

const normalizeFollowingQuestionKeys = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string')
    }

    if (!value || typeof value !== 'object') {
        return []
    }

    return Object.entries(value).reduce((acc, [key, item]) => {
        if (typeof item === 'string') {
            acc.push(key)
            return acc
        }

        if (item && typeof item === 'object') {
            const questionId = (item as any).question_id
            if (typeof questionId === 'string') {
                acc.push(key)
            }
        }

        return acc
    }, [] as string[])
}

const normalizeFlowDataFromContextQuestions = (questions: any[]): FlowData => {
    const normalized: FlowData = {}

    ;(questions || []).forEach((questionNode) => {
        const questionData = questionNode?.question_data || questionNode
        if (!questionData?.id) return

        const followingFromNode = normalizeFollowingQuestions(
            questionNode?.following_questions
        )
        const followingFromQuestionData = normalizeFollowingQuestions(
            questionData?.following_questions
        )
        const followingQuestionKeysFromNode = normalizeFollowingQuestionKeys(
            questionNode?.following_questions
        )
        const followingQuestionKeysFromQuestionData =
            normalizeFollowingQuestionKeys(questionData?.following_questions)
        const followingQuestions =
            followingFromNode.length > 0
                ? followingFromNode
                : followingFromQuestionData
        const followingQuestionKeys =
            followingQuestionKeysFromNode.length > 0
                ? followingQuestionKeysFromNode
                : followingQuestionKeysFromQuestionData

        const dependsOnFromQuestionData = Array.isArray(questionData.depends_on)
            ? questionData.depends_on
                  .map((dependency: any) =>
                      typeof dependency === 'string'
                          ? dependency
                          : dependency?.question_id
                  )
                  .filter(Boolean)
            : []

        const dependsOnFromQuestionNode = Array.isArray(
            questionNode?.depends_on
        )
            ? questionNode.depends_on
                  .map((dependency: any) =>
                      typeof dependency === 'string'
                          ? dependency
                          : dependency?.question_id
                  )
                  .filter(Boolean)
            : []

        const dependsOn = Array.from(
            new Set([
                ...dependsOnFromQuestionNode,
                ...dependsOnFromQuestionData,
            ])
        )

        normalized[questionData.id] = {
            ...questionData,
            head: Boolean(questionNode?.head ?? questionData.head),
            following_questions: followingQuestions,
            following_question_keys: followingQuestionKeys,
            depends_on: dependsOn,
        }
    })

    return normalized
}

const normalizeFlowData = (data: FlowData): FlowData => {
    const normalized: FlowData = {}

    Object.entries(data || {}).forEach(([key, question]) => {
        if (!question) return
        const mapKey = question.id || key
        normalized[mapKey] = question
    })

    return normalized
}

const calculateTreePositions = (
    data: FlowData
): Record<string, {x: number; y: number}> => {
    const normalizedData = normalizeFlowData(data)
    const nodeIds = Object.keys(normalizedData)

    if (nodeIds.length === 0) {
        return {}
    }

    const headNodes = nodeIds.filter((id) => normalizedData[id].head)
    const noDependencyNodes = nodeIds.filter(
        (id) => (normalizedData[id].depends_on || []).length === 0
    )

    const rootCandidates = headNodes.length
        ? headNodes
        : noDependencyNodes.length
          ? noDependencyNodes
          : [nodeIds[0]]

    const depthMap: Record<string, number> = {}
    const queue: Array<{id: string; depth: number}> = []

    rootCandidates.forEach((rootId) => {
        if (!normalizedData[rootId]) return
        depthMap[rootId] = 0
        queue.push({id: rootId, depth: 0})
    })

    while (queue.length > 0) {
        const current = queue.shift()!
        const currentNode = normalizedData[current.id]
        if (!currentNode) continue

        const children = currentNode.following_questions || []
        children.forEach((childId) => {
            if (!normalizedData[childId]) return
            const nextDepth = current.depth + 1
            if (
                depthMap[childId] === undefined ||
                nextDepth < depthMap[childId]
            ) {
                depthMap[childId] = nextDepth
                queue.push({id: childId, depth: nextDepth})
            }
        })
    }

    const assignedDepths = Object.values(depthMap)
    const maxAssignedDepth = assignedDepths.length
        ? Math.max(...assignedDepths)
        : 0

    nodeIds.forEach((id) => {
        if (depthMap[id] === undefined) {
            depthMap[id] = maxAssignedDepth + 1
        }
    })

    const levels = new Map<number, string[]>()
    Object.entries(depthMap).forEach(([id, depth]) => {
        if (!levels.has(depth)) {
            levels.set(depth, [])
        }
        levels.get(depth)!.push(id)
    })

    levels.forEach((levelNodes, _depth) => {
        levelNodes.sort((a, b) => {
            const labelA = normalizedData[a]?.label || ''
            const labelB = normalizedData[b]?.label || ''
            return labelA.localeCompare(labelB)
        })
    })

    const positions: Record<string, {x: number; y: number}> = {}
    let minY = Infinity

    Array.from(levels.entries())
        .sort(([depthA], [depthB]) => depthA - depthB)
        .forEach(([depth, levelNodes]) => {
            const offset = ((levelNodes.length - 1) * TREE_VERTICAL_SPACING) / 2
            levelNodes.forEach((nodeId, index) => {
                const x = depth * TREE_HORIZONTAL_SPACING
                const y = index * TREE_VERTICAL_SPACING - offset
                positions[nodeId] = {x, y}
                if (y < minY) {
                    minY = y
                }
            })
        })

    if (minY !== Infinity && minY < 0) {
        const shift = Math.abs(minY) + TREE_VERTICAL_SPACING
        Object.keys(positions).forEach((nodeId) => {
            positions[nodeId].y += shift
        })
    }

    return positions
}

// Interface local para os nós do ReactFlow
interface Question {
    id: string
    titulo: string
    prompt: string
    tipo:
        | 'text'
        | 'select'
        | 'multiSelect'
        | 'table'
        | 'radio'
        | 'checkbox'
        | 'autonomous'
        | 'table_autonomous'
        | 'document_upload'
    conteudo: string | Record<string, string>
    followed: string[]
    depends_on?: string[]
    following_question_keys?: string[]
    created_at?: string
    instruction?: string | null
    tooltip?: string | null
    optional?: boolean
    originalData?: FlowQuestion // Manter referência aos dados originais
}

const isOptionBasedQuestionType = (questionType: Question['tipo']) => {
    return ['select', 'multiSelect', 'radio', 'checkbox'].includes(questionType)
}

const isBranchingQuestionType = (questionType: Question['tipo']) => {
    return isOptionBasedQuestionType(questionType) || questionType === 'autonomous'
}

const getDefaultFollowingQuestionKey = (index: number) => {
    return index === 0 ? 'Default' : `Default ${index + 1}`
}

const initialNodes: Node[] = []
const initialEdges: Edge[] = []

const getQuestionIcon = (questionType: Question['tipo']) => {
    switch (questionType) {
        case 'autonomous':
            return <SmartToySharpIcon fontSize="small" />
        case 'text':
            return <TextFieldsSharpIcon fontSize="small" />
        case 'select':
            return <ListSharpIcon fontSize="small" />
        case 'multiSelect':
        case 'checkbox':
            return <ChecklistRtlSharpIcon fontSize="small" />
        case 'table':
            return <TableChartSharpIcon fontSize="small" />
        case 'table_autonomous':
            return <AutoAwesomeSharpIcon fontSize="small" />
        case 'document_upload':
            return <UploadFileSharpIcon fontSize="small" />
        default:
            return <WidgetsIcon fontSize="small" />
    }
}

const getQuestionColor = (questionType: Question['tipo']) => {
    switch (questionType) {
        case 'autonomous':
        case 'table_autonomous':
            return {bg: '#3294EF', fg: '#ffffff'}
        case 'select':
        case 'multiSelect':
        case 'radio':
        case 'checkbox':
            return {bg: '#3ACDB3', fg: '#063a30'}
        case 'table':
            return {bg: '#e8912a', fg: '#ffffff'}
        case 'document_upload':
            return {bg: '#2fb87a', fg: '#ffffff'}
        default:
            return {bg: '#f2f5f6', fg: '#8a969c'}
    }
}

const getQuestionTypeLabel = (questionType: Question['tipo']) => {
    switch (questionType) {
        case 'text':
            return 'Texto'
        case 'select':
            return 'Select'
        case 'multiSelect':
            return 'MultiSelect'
        case 'radio':
            return 'Radio'
        case 'checkbox':
            return 'Checkbox'
        case 'table':
            return 'Tabela'
        case 'autonomous':
            return 'Autônomo'
        case 'table_autonomous':
            return 'Tabela Autônoma'
        case 'document_upload':
            return 'Upload de Documento'
        default:
            return String(questionType)
    }
}

const buildNodeLabel = (text: string, questionType: Question['tipo']) => {
    const {bg, fg} = getQuestionColor(questionType)

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
            }}
        >
            <span
                style={{
                    width: '36px',
                    height: '36px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '9px',
                    backgroundColor: bg,
                    color: fg,
                    flexShrink: 0,
                }}
            >
                {getQuestionIcon(questionType)}
            </span>
            <div style={{flex: 1, minWidth: 0}}>
                <div
                    style={{
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: '#0c1f29',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {text}
                </div>
                <div style={{marginTop: '5px'}}>
                    <span
                        style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            padding: '2px 7px',
                            borderRadius: '20px',
                            backgroundColor: '#f2f5f6',
                            color: '#5d6b72',
                        }}
                    >
                        {getQuestionTypeLabel(questionType)}
                    </span>
                </div>
            </div>
        </div>
    )
}

interface EdgeActions {
    requestDelete: (edge: Edge) => void
    requestEdit: (edge: Edge) => void
    getKeyLabel: (edge: Edge) => string
}

const EdgeActionsContext = createContext<EdgeActions>({
    requestDelete: () => {},
    requestEdit: () => {},
    getKeyLabel: () => '',
})

function DeletableEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    source,
    target,
    sourceHandleId,
    targetHandleId,
}: EdgeProps) {
    const [isHovered, setIsHovered] = useState(false)
    const {requestDelete, requestEdit, getKeyLabel} =
        useContext(EdgeActionsContext)

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    })

    const buildEdge = () =>
        ({
            id,
            source,
            target,
            sourceHandle: sourceHandleId,
            targetHandle: targetHandleId,
        }) as Edge

    const keyLabel = getKeyLabel(buildEdge())

    return (
        <>
            <BaseEdge
                id={id}
                path={edgePath}
                style={{
                    ...style,
                    stroke: isHovered ? '#dc3545' : style?.stroke,
                }}
                markerEnd={markerEnd}
            />
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                style={{pointerEvents: 'stroke', cursor: 'pointer'}}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            />
            {keyLabel && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 18}px)`,
                            pointerEvents: 'none',
                            background: 'white',
                            border: '1px solid #d6dde0',
                            borderRadius: '20px',
                            padding: '2px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#5d6b72',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 2px rgba(12,31,41,.08)',
                        }}
                    >
                        {keyLabel}
                    </div>
                </EdgeLabelRenderer>
            )}
            {isHovered && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'all',
                            display: 'flex',
                            gap: '4px',
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <button
                            onClick={(event) => {
                                event.stopPropagation()
                                requestEdit(buildEdge())
                            }}
                            title="Editar chave da ligação"
                            style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: '1px solid #007bff',
                                backgroundColor: 'white',
                                color: '#007bff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                lineHeight: 1,
                                padding: 0,
                            }}
                        >
                            ✎
                        </button>
                        <button
                            onClick={(event) => {
                                event.stopPropagation()
                                requestDelete(buildEdge())
                            }}
                            title="Remover ligação"
                            style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: '1px solid #dc3545',
                                backgroundColor: 'white',
                                color: '#dc3545',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                lineHeight: 1,
                                padding: 0,
                            }}
                        >
                            ×
                        </button>
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    )
}

interface NodeActions {
    requestEdit: (nodeId: string) => void
    requestDelete: (nodeId: string) => void
    requestClone: (nodeId: string) => void
}

const NodeActionsContext = createContext<NodeActions>({
    requestEdit: () => {},
    requestDelete: () => {},
    requestClone: () => {},
})

const nodeToolbarBtnStyle = (color: string): React.CSSProperties => ({
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    lineHeight: 1,
    padding: 0,
})

function FlowQuestionNode({id, data, sourcePosition, targetPosition}: NodeProps) {
    const [isHovered, setIsHovered] = useState(false)
    const [isHandleHovered, setIsHandleHovered] = useState(false)
    const {requestEdit, requestDelete, requestClone} =
        useContext(NodeActionsContext)

    return (
        <div
            style={{
                position: 'relative',
                width: '236px',
                backgroundColor: '#ffffff',
                border: `1.5px solid ${isHovered ? '#cfe3f8' : '#e6eaec'}`,
                borderRadius: '12px',
                boxShadow: isHovered
                    ? '0 6px 20px rgba(12, 31, 41, 0.07)'
                    : '0 1px 2px rgba(12, 31, 41, 0.05)',
                transition: 'box-shadow .13s, border-color .13s',
                cursor: 'grab',
                userSelect: 'none',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Handle
                type="target"
                position={targetPosition ?? Position.Left}
                style={{opacity: 0}}
            />
            <Handle
                type="source"
                position={sourcePosition ?? Position.Right}
                onMouseEnter={() => setIsHandleHovered(true)}
                onMouseLeave={() => setIsHandleHovered(false)}
                style={{
                    width: '15px',
                    height: '15px',
                    borderRadius: '50%',
                    backgroundColor: isHandleHovered ? '#3294EF' : '#ffffff',
                    border: `2px solid ${isHandleHovered ? '#3294EF' : '#8a969c'}`,
                    boxShadow: isHandleHovered
                        ? '0 0 0 4px #eaf3fd'
                        : 'none',
                    transition: 'all .12s',
                }}
            />
            <div
                className="nodrag"
                style={{
                    position: 'absolute',
                    top: '-13px',
                    right: '12px',
                    display: 'flex',
                    gap: '2px',
                    backgroundColor: 'white',
                    border: '1px solid #d6dde0',
                    borderRadius: '20px',
                    padding: '3px',
                    boxShadow: '0 6px 20px rgba(12, 31, 41, 0.14)',
                    opacity: isHovered ? 1 : 0,
                    pointerEvents: isHovered ? 'auto' : 'none',
                    transition: 'opacity .12s',
                    zIndex: 20,
                }}
            >
                <button
                    onClick={(event) => {
                        event.stopPropagation()
                        requestEdit(id)
                    }}
                    title="Editar pergunta"
                    style={nodeToolbarBtnStyle('#33454e')}
                >
                    ✎
                </button>
                <button
                    onClick={(event) => {
                        event.stopPropagation()
                        requestClone(id)
                    }}
                    title="Clonar pergunta"
                    style={nodeToolbarBtnStyle('#33454e')}
                >
                    <ContentCopyOutlinedIcon sx={{fontSize: 13}} />
                </button>
                <button
                    onClick={(event) => {
                        event.stopPropagation()
                        requestDelete(id)
                    }}
                    title="Apagar pergunta"
                    style={nodeToolbarBtnStyle('#dc3545')}
                >
                    ×
                </button>
            </div>
            <div style={{padding: '13px 14px'}}>
                {data.label as React.ReactNode}
            </div>
        </div>
    )
}

export default function FlowCreator() {
    const navigate = useNavigate()
    const {
        flowQuestions: contextFlowData,
        flows: rawFlows,
        setFlowData,
        setFlows,
        getFlow,
    } = useContext(HistoryContext)
    const flows = Array.isArray(rawFlows) ? rawFlows : []
    const flowWrapperRef = useRef<HTMLDivElement | null>(null)
    const flowApi = useMemo(() => new FlowApi(), [])
    const [nodes, setNodes] = useState(initialNodes)
    const [edges, setEdges] = useState(initialEdges)
    const [showModal, setShowModal] = useState(false)
    const [showModalFlow, setShowModalFlow] = useState(false)
    const [showCreateFlowModal, setShowCreateFlowModal] = useState(false)
    const [showCloneFlowModal, setShowCloneFlowModal] = useState(false)
    const [showDeleteQuestionConfirm, setShowDeleteQuestionConfirm] =
        useState(false)
    const [edgeToDelete, setEdgeToDelete] = useState<Edge | null>(null)
    const [edgeToEdit, setEdgeToEdit] = useState<Edge | null>(null)
    const [edgeEditKeyDraft, setEdgeEditKeyDraft] = useState('')
    const [selectedNodeForModal, setSelectedNodeForModal] =
        useState<Node | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isCreatingFlow, setIsCreatingFlow] = useState(false)

    const [nodeTitle, setNodeTitle] = useState('')
    const [questionType, setQuestionType] = useState<
        | 'text'
        | 'select'
        | 'multiSelect'
        | 'table'
        | 'radio'
        | 'checkbox'
        | 'autonomous'
        | 'table_autonomous'
        | 'document_upload'
    >('text')
    const [questionContent, setQuestionContent] = useState<string>('')
    const [questionOptional, setQuestionOptional] = useState(false)
    const [autonomousInstruction, setAutonomousInstruction] = useState('')
    const [questionTooltip, setQuestionTooltip] = useState('')
    const [optionsDict, setOptionsDict] = useState<Record<string, string>>({})
    const [draggedOptionKey, setDraggedOptionKey] = useState<string | null>(
        null
    )
    const [dragOverOptionKey, setDragOverOptionKey] = useState<string | null>(
        null
    )
    const [followedNodes, setFollowedNodes] = useState<string[]>([])
    const [dependsOnNodes, setDependsOnNodes] = useState<string[]>([])
    const [followedNodeKeys, setFollowedNodeKeys] = useState<string[]>([])
    const [flowFilter, setFlowFilter] = useState('')
    const [newFlowName, setNewFlowName] = useState('')
    const [cloneFlowName, setCloneFlowName] = useState('')
    const [cloneFlowFilter, setCloneFlowFilter] = useState('')
    const [selectedFlowToCloneId, setSelectedFlowToCloneId] = useState('')
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'error'
    >('success')
    const [flowValidationErrors, setFlowValidationErrors] = useState<
        string[]
    >([])

    const showFeedback = useCallback(
        (message: string, severity: 'success' | 'error') => {
            setSnackbarMessage(message)
            setSnackbarSeverity(severity)
            setSnackbarOpen(true)
        },
        []
    )

    const handleCloseSnackbar = useCallback(
        (_event?: Event | React.SyntheticEvent, reason?: string) => {
            if (reason === 'clickaway') {
                return
            }
            setSnackbarOpen(false)
        },
        []
    )

    const flowQuestions = useMemo<QuestionFlow | null>(() => {
        if (!contextFlowData) {
            return null
        }

        const incomingByNodeId: Record<string, number> = {}
        edges.forEach((edge) => {
            incomingByNodeId[edge.target] =
                (incomingByNodeId[edge.target] || 0) + 1
        })

        const originalByQuestionId = new Map<string, QuestionNode>()
        ;(contextFlowData.questions || []).forEach((questionNode) => {
            const questionId =
                questionNode?.question_data?.id || questionNode?.id
            if (questionId) {
                originalByQuestionId.set(questionId, questionNode)
            }
        })

        const convertedQuestions: QuestionNode[] = nodes.map((node) => {
            const localQuestion = node.data.question as Question
            const originalQuestionNode = originalByQuestionId.get(node.id)
            const originalData =
                originalQuestionNode?.question_data ||
                localQuestion?.originalData
            const originalDataAny = originalData as any

            const normalizedDependsOn: Dependency[] = (
                localQuestion?.depends_on ||
                originalQuestionNode?.depends_on ||
                originalDataAny?.depends_on ||
                []
            ).map((dependency: any) => {
                if (typeof dependency === 'string') {
                    return {question_id: dependency}
                }

                return dependency
            })

            const followedFromEdges = edges
                .filter((edge) => edge.source === node.id)
                .map((edge) => edge.target)

            const followedIds =
                (localQuestion?.followed || []).length > 0
                    ? localQuestion.followed
                    : followedFromEdges

            const normalizedFollowed = followedIds.filter((targetId) =>
                nodes.some((candidate) => candidate.id === targetId)
            )

            const optionBasedType = isOptionBasedQuestionType(
                localQuestion?.tipo
            )

            const optionContentKeys =
                optionBasedType &&
                localQuestion?.conteudo &&
                typeof localQuestion.conteudo === 'object'
                    ? Object.values(localQuestion.conteudo)
                          .map((value) => String(value).trim())
                          .filter((value) => value !== '')
                    : []

            const customFollowingKeys =
                localQuestion?.following_question_keys || []

            const followingQuestionsRecord = normalizedFollowed.reduce(
                (acc, targetId, index) => {
                    const customKey = (customFollowingKeys[index] || '').trim()
                    const key =
                        customKey ||
                        (optionBasedType && optionContentKeys[index]
                            ? optionContentKeys[index]
                            : index === 0
                              ? 'Default'
                              : `Default ${index + 1}`)

                    acc[key] = targetId
                    return acc
                },
                {} as Record<string, string>
            )

            const convertedData = convertLocalQuestionToFlowData(localQuestion)

            const mappedQuestionType =
                convertedData.question_type ||
                (localQuestion.tipo === 'multiSelect'
                    ? 'multiselect'
                    : localQuestion.tipo)

            return {
                id: originalQuestionNode?.id || node.id,
                head:
                    incomingByNodeId[node.id] === undefined ||
                    incomingByNodeId[node.id] === 0,
                following_questions: followingQuestionsRecord,
                depends_on: normalizedDependsOn,
                question_data: {
                    id: node.id,
                    created_at:
                        originalDataAny?.created_at ||
                        localQuestion?.created_at ||
                        new Date().toISOString(),
                    is_active: originalDataAny?.is_active ?? true,
                    name: originalDataAny?.name ?? null,
                    content:
                        localQuestion.prompt ||
                        (convertedData.content as string) ||
                        originalDataAny?.content ||
                        '',
                    label: convertedData.label || localQuestion.titulo,
                    instruction:
                        convertedData.instruction ??
                        originalDataAny?.instruction ??
                        null,
                    tooltip:
                        convertedData.tooltip ??
                        originalDataAny?.tooltip ??
                        null,
                    assistant_id: originalDataAny?.assistant_id ?? null,
                    question_type: mappedQuestionType,
                    type: convertedData.type ||
                        originalDataAny?.type || {
                            text: {
                                placeholder: null,
                                default: null,
                                allowed_answer_type: 'text',
                            },
                        },
                    depends_on: normalizedDependsOn,
                    document_section: originalDataAny?.document_section ?? null,
                    optional: convertedData.optional ?? originalDataAny?.optional ?? false,
                    following_questions: normalizedFollowed,
                    head:
                        incomingByNodeId[node.id] === undefined ||
                        incomingByNodeId[node.id] === 0,
                },
            }
        })

        return {
            ...contextFlowData,
            questions: convertedQuestions,
        }
    }, [contextFlowData, nodes, edges])

    const handleUpdateFlow = async () => {
        if (!flowQuestions) {
            return
        }

        const {errors: _errors, ...flowQuestionsWithoutErrors} = flowQuestions

        const normalizeDependsOnToIds = (dependsOn: any): string[] => {
            if (!Array.isArray(dependsOn)) {
                return []
            }

            return dependsOn
                .map((dependency: any) => {
                    if (typeof dependency === 'string') {
                        return dependency
                    }

                    if (dependency && typeof dependency === 'object') {
                        return dependency.question_id
                    }

                    return null
                })
                .filter((dependencyId): dependencyId is string =>
                    Boolean(dependencyId)
                )
        }

        const questionsWithDependsOnIds = (
            flowQuestionsWithoutErrors.questions || []
        ).map((question: any) => {
            const normalizedDependsOn = normalizeDependsOnToIds(
                question?.depends_on
            )

            return {
                ...question,
                depends_on: normalizedDependsOn,
                question_data: {
                    ...question?.question_data,
                    depends_on: normalizeDependsOnToIds(
                        question?.question_data?.depends_on
                    ),
                },
            }
        })

        try {
            const response = await flowApi.updateFlow({
                document_flow: {
                    ...flowQuestionsWithoutErrors,
                    questions: questionsWithDependsOnIds,
                },
            })

            const validationErrors = [
                response?.has_following_cycle,
                response?.is_connected,
                response?.has_dependencies_cycle,
            ].filter(
                (validationError): validationError is string =>
                    typeof validationError === 'string' &&
                    validationError.length > 0
            )

            if (validationErrors.length > 0) {
                setFlowValidationErrors(validationErrors)
            } else {
                showFeedback('Fluxo salvo com sucesso.', 'success')
            }
        } catch (error) {
            console.error('Erro ao salvar fluxo:', error)
            showFeedback('Falha ao salvar o fluxo.', 'error')
            navigate('/error')
        }
    }

    const handleDownloadFlowImage = useCallback(async () => {
        try {
            if (nodes.length === 0) {
                showFeedback(
                    'Não há perguntas no fluxo para exportar.',
                    'error'
                )
                return
            }

            const viewportElement = flowWrapperRef.current?.querySelector(
                '.react-flow__viewport'
            ) as HTMLElement | null

            if (!viewportElement) {
                showFeedback(
                    'Não foi possível localizar o fluxo para baixar a imagem.',
                    'error'
                )
                return
            }

            const nodesBounds = getNodesBounds(nodes)
            const exportPadding = 80
            const exportBounds = {
                x: nodesBounds.x - exportPadding,
                y: nodesBounds.y - exportPadding,
                width: nodesBounds.width + exportPadding * 2,
                height: nodesBounds.height + exportPadding * 2,
            }

            const exportWidth = Math.max(1, Math.ceil(exportBounds.width))
            const exportHeight = Math.max(1, Math.ceil(exportBounds.height))

            const viewport = getViewportForBounds(
                exportBounds,
                exportWidth,
                exportHeight,
                0.1,
                1,
                0
            )

            const dataUrl = await toPng(viewportElement, {
                backgroundColor: '#ffffff',
                cacheBust: true,
                pixelRatio: 2,
                width: exportWidth,
                height: exportHeight,
                style: {
                    width: `${exportWidth}px`,
                    height: `${exportHeight}px`,
                    transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
                    transformOrigin: 'top left',
                },
            })

            const baseName = (contextFlowData?.name || 'fluxo')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-_]/g, '')
            const fileName = `${baseName || 'fluxo'}-${new Date()
                .toISOString()
                .slice(0, 10)}.png`

            const link = document.createElement('a')
            link.download = fileName
            link.href = dataUrl
            link.click()

            showFeedback('Imagem do fluxo baixada com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao baixar imagem do fluxo:', error)
            showFeedback('Falha ao baixar imagem do fluxo.', 'error')
            navigate('/error')
        }
    }, [contextFlowData?.name, nodes, showFeedback])

    // Função para converter os dados do flow em nós do ReactFlow
    const convertFlowDataToNodes = useCallback(
        (data: FlowData): {nodes: Node[]; edges: Edge[]} => {
            const normalizedData = normalizeFlowData(data)
            const newNodes: Node[] = []
            const newEdges: Edge[] = []
            const nodePositions = calculateTreePositions(normalizedData)

            Object.values(normalizedData).forEach((questionData) => {
                // Converter tipo de pergunta
                let localQuestionType: Question['tipo'] = 'text'
                let content: string | Record<string, string> =
                    questionData.content
                const promptContent = questionData.content || ''

                switch (questionData.question_type) {
                    case 'text':
                        localQuestionType = 'text'
                        break
                    case 'select':
                        localQuestionType = 'select'
                        if (questionData.type.select?.options) {
                            const optionsDict: Record<string, string> = {}
                            questionData.type.select.options.forEach(
                                (option) => {
                                    optionsDict[option.title] = option.content
                                }
                            )
                            content = optionsDict
                        }
                        break
                    case 'multiselect':
                        localQuestionType = 'multiSelect'
                        if (questionData.type.multiselect?.options) {
                            const optionsDict: Record<string, string> = {}
                            questionData.type.multiselect.options.forEach(
                                (option) => {
                                    optionsDict[option.title] = option.content
                                }
                            )
                            content = optionsDict
                        }
                        break
                    case 'table':
                        localQuestionType = 'table'
                        if (questionData.type.table?.headers) {
                            const headersDict: Record<string, string> = {}
                            questionData.type.table.headers.forEach(
                                (header) => {
                                    headersDict[header.label] = header.name
                                }
                            )
                            content = headersDict
                        } else {
                            content = questionData.content
                        }
                        break
                    case 'autonomous':
                        localQuestionType = 'autonomous'
                        break
                    case 'document_upload':
                        localQuestionType = 'document_upload'
                        break
                    default:
                        localQuestionType = 'text'
                }

                const question: Question = {
                    id: questionData.id,
                    titulo: questionData.label,
                    prompt: promptContent,
                    tipo: localQuestionType,
                    conteudo: content,
                    followed: questionData.following_questions || [],
                    following_question_keys:
                        questionData.following_question_keys || [],
                    created_at: questionData.created_at,
                    instruction: questionData.instruction ?? '',
                    tooltip: questionData.tooltip ?? '',
                    optional: Boolean(questionData.optional),
                    originalData: questionData,
                }

                const node: Node = {
                    id: questionData.id,
                    position: nodePositions[questionData.id] || {
                        x: Math.random() * 500,
                        y: Math.random() * 500,
                    },
                    data: {
                        label: buildNodeLabel(
                            questionData.label,
                            localQuestionType
                        ),
                        rawLabel: questionData.label,
                        question: question,
                        isHead: Boolean(questionData.head),
                    },
                    type: 'questionNode',
                    style: getNodeStyle(localQuestionType),
                    sourcePosition: Position.Right,
                    targetPosition: Position.Left,
                }

                newNodes.push(node)

                // Criar edges baseadas nas following_questions
                const createdEdgeIds = new Set<string>()
                questionData.following_questions?.forEach((targetId) => {
                    if (!normalizedData[targetId]) return
                    const edgeId = `${questionData.id}-${targetId}`
                    if (createdEdgeIds.has(edgeId)) return

                    createdEdgeIds.add(edgeId)
                    const edge: Edge = {
                        id: edgeId,
                        source: questionData.id,
                        target: targetId,
                        type: 'deletable',
                    }
                    newEdges.push(edge)
                })
            })

            return {nodes: newNodes, edges: newEdges}
        },
        []
    )

    // Carregar dados do flow
    useEffect(() => {
        getFlow()
    }, [])

    useEffect(() => {
        if (contextFlowData?.questions?.length) {
            const normalizedContextFlow = normalizeFlowDataFromContextQuestions(
                contextFlowData.questions
            )
            const {nodes: contextNodes, edges: contextEdges} =
                convertFlowDataToNodes(normalizedContextFlow)

            setNodes(contextNodes)
            setEdges(contextEdges)
            setIsLoading(false)
            return
        }

        setNodes([])
        setEdges([])
        setIsLoading(false)
    }, [contextFlowData, convertFlowDataToNodes])

    useEffect(() => {
        const expectedEdges: Edge[] = []
        const expectedEdgeIdSet = new Set<string>()

        nodes.forEach((node) => {
            const question = node.data.question as Question
            const followedIds = question?.followed || []

            followedIds.forEach((targetId) => {
                const targetExists = nodes.some((n) => n.id === targetId)

                if (targetExists) {
                    const edgeId = `${node.id}-${targetId}`
                    if (expectedEdgeIdSet.has(edgeId)) {
                        return
                    }

                    expectedEdgeIdSet.add(edgeId)
                    expectedEdges.push({
                        id: edgeId,
                        source: node.id,
                        target: targetId,
                        type: 'deletable',
                    })
                }
            })
        })

        const currentEdgeIds = edges.map((edge) => edge.id).sort()
        const expectedEdgeIdsList = expectedEdges.map((edge) => edge.id).sort()

        const edgesChanged =
            JSON.stringify(currentEdgeIds) !==
            JSON.stringify(expectedEdgeIdsList)

        if (edgesChanged) {
            setEdges(expectedEdges)
        }
    }, [nodes])

    useEffect(() => {
        let needsUpdate = false

        const updatedNodes = nodes.map((node) => {
            const question = node.data.question as Question
            const currentFollowed = question?.followed || []

            const edgeBasedFollowed = edges
                .filter((edge) => edge.source === node.id)
                .map((edge) => edge.target)

            const edgeBasedSet = new Set(edgeBasedFollowed)
            const preservedOrderFollowed = currentFollowed.filter((targetId) =>
                edgeBasedSet.has(targetId)
            )
            const newTargetsFromEdges = edgeBasedFollowed.filter(
                (targetId) => !preservedOrderFollowed.includes(targetId)
            )
            const nextFollowed = [
                ...preservedOrderFollowed,
                ...newTargetsFromEdges,
            ]

            const followedChanged =
                JSON.stringify(currentFollowed) !== JSON.stringify(nextFollowed)

            if (followedChanged) {
                console.log(`📝 Atualizando followed do nó ${node.id}:`)
                console.log(`  - Antes: [${currentFollowed.join(', ')}]`)
                console.log(`  - Depois: [${nextFollowed.join(', ')}]`)

                needsUpdate = true

                return {
                    ...node,
                    data: {
                        ...node.data,
                        question: {
                            ...question,
                            id: question?.id || node.id,
                            titulo:
                                question?.titulo ||
                                (node.data.rawLabel as string),
                            prompt: question?.prompt || '',
                            tipo: question?.tipo || 'text',
                            conteudo: question?.conteudo || '',
                            followed: nextFollowed,
                            depends_on: question?.depends_on || [],
                        },
                    },
                }
            }

            return node
        })

        if (needsUpdate) {
            setNodes(updatedNodes)
        }
    }, [edges])

    const getNodeStyle = (_questionType: Question['tipo']) => {
        return {}
    }

    // Função para converter questão local de volta para o formato original
    function convertLocalQuestionToFlowData(
        question: Question
    ): Partial<FlowQuestion> {
        const baseData: Partial<FlowQuestion> = {
            content: question.prompt,
            label: question.titulo,
            question_type:
                question.tipo === 'multiSelect' ? 'multiselect' : question.tipo,
            following_questions: question.followed,
            depends_on: question.depends_on || [],
            optional: Boolean(question.optional),
            tooltip: question.tooltip ?? null,
        }

        // Configurar type baseado no tipo da pergunta
        switch (question.tipo) {
            case 'text':
                baseData.type = {
                    text: {
                        placeholder: null,
                        default: null,
                        allowed_answer_type: 'text',
                    },
                }
                break
            case 'select':
            case 'multiSelect':
                if (typeof question.conteudo === 'object') {
                    const options: QuestionOption[] = Object.entries(
                        question.conteudo
                    ).map(([title, content], index) => ({
                        title,
                        content,
                        index,
                    }))

                    const selectType = {
                        options,
                        default: -1,
                        placeholder: `Selecione uma opção...`,
                        allowed_answer_type: 'text',
                    }

                    baseData.type =
                        question.tipo === 'select'
                            ? {select: selectType}
                            : {multiselect: selectType}
                }
                break
            case 'table':
                {
                    const headersFromContent =
                        typeof question.conteudo === 'object'
                            ? Object.entries(question.conteudo)
                                  .map(([label, name]) => ({
                                      label: label.trim(),
                                      name: String(name).trim(),
                                      size: 1,
                                  }))
                                  .filter(
                                      (header) =>
                                          header.label !== '' &&
                                          header.name !== ''
                                  )
                            : []

                    baseData.type = {
                        table: {
                            headers:
                                headersFromContent.length > 0
                                    ? headersFromContent
                                    : [
                                          {
                                              label: 'name',
                                              name: 'Nome do Item',
                                              size: 1,
                                          },
                                          {
                                              label: 'description',
                                              name: 'Descrição',
                                              size: 1,
                                          },
                                          {
                                              label: 'quantity',
                                              name: 'Quantidade',
                                              size: 1,
                                          },
                                          {
                                              label: 'price',
                                              name: 'Preço',
                                              size: 1,
                                          },
                                      ],
                            items: [],
                            type: 'default',
                        },
                    }
                }
                break
            case 'autonomous':
                baseData.type = {autonomous: true}
                baseData.instruction = question.instruction || ''
                break
            case 'document_upload':
                baseData.type = {document_upload: {documents: []}}
                baseData.instruction = question.instruction || ''
                break
        }

        return baseData
    }

    const onElementsRemove = useCallback((elementsToRemove: any[]) => {
        setNodes((nds) =>
            nds.filter(
                (node) => !elementsToRemove.some((el) => el.id === node.id)
            )
        )
        setEdges((eds) =>
            eds.filter(
                (edge) => !elementsToRemove.some((el) => el.id === edge.id)
            )
        )
    }, [])

    const requestDeleteEdge = useCallback((edge: Edge) => {
        setEdgeToDelete(edge)
    }, [])

    const cancelDeleteEdge = useCallback(() => {
        setEdgeToDelete(null)
    }, [])

    const confirmDeleteEdge = useCallback(() => {
        if (!edgeToDelete) return

        setEdges((eds) => eds.filter((edge) => edge.id !== edgeToDelete.id))
        setEdgeToDelete(null)
    }, [edgeToDelete])

    const getEdgeKeyValue = useCallback(
        (edge: Edge) => {
            const sourceNode = nodes.find((node) => node.id === edge.source)
            const question = sourceNode?.data.question as Question | undefined
            const index = question?.followed?.indexOf(edge.target) ?? -1
            return index >= 0
                ? question?.following_question_keys?.[index] || ''
                : ''
        },
        [nodes]
    )

    const requestEditEdge = useCallback(
        (edge: Edge) => {
            setEdgeEditKeyDraft(getEdgeKeyValue(edge))
            setEdgeToEdit(edge)
        },
        [getEdgeKeyValue]
    )

    const cancelEditEdge = useCallback(() => {
        setEdgeToEdit(null)
        setEdgeEditKeyDraft('')
    }, [])

    const confirmEditEdge = useCallback(() => {
        if (!edgeToEdit) return

        const newKey = edgeEditKeyDraft.trim()
        if (newKey === '') {
            showFeedback('Informe uma chave para a ligação.', 'error')
            return
        }

        const sourceNode = nodes.find((node) => node.id === edgeToEdit.source)
        const question = sourceNode?.data.question as Question | undefined
        const index = question?.followed?.indexOf(edgeToEdit.target) ?? -1

        if (!sourceNode || !question || index === -1) {
            setEdgeToEdit(null)
            return
        }

        const siblingKeys = (question.following_question_keys || []).filter(
            (_, keyIndex) => keyIndex !== index
        )
        if (siblingKeys.includes(newKey)) {
            showFeedback(
                'As chaves de Próximos Nós devem ser únicas.',
                'error'
            )
            return
        }

        setNodes((nds) =>
            nds.map((node) => {
                if (node.id !== sourceNode.id) return node

                const updatedKeys = [...(question.following_question_keys || [])]
                updatedKeys[index] = newKey

                return {
                    ...node,
                    data: {
                        ...node.data,
                        question: {
                            ...question,
                            following_question_keys: updatedKeys,
                        },
                    },
                }
            })
        )

        setEdgeToEdit(null)
        setEdgeEditKeyDraft('')
    }, [edgeToEdit, edgeEditKeyDraft, nodes, showFeedback])

    const edgeActions = useMemo(
        () => ({
            requestDelete: requestDeleteEdge,
            requestEdit: requestEditEdge,
            getKeyLabel: getEdgeKeyValue,
        }),
        [requestDeleteEdge, requestEditEdge, getEdgeKeyValue]
    )

    const edgeTypes = useMemo(() => ({deletable: DeletableEdge}), [])

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Delete') {
                const selectedNodes = nodes.filter((node) => node.selected)
                const selectedEdges = edges.filter((edge) => edge.selected)
                const elementsToRemove = [...selectedNodes, ...selectedEdges]

                if (elementsToRemove.length > 0) {
                    onElementsRemove(elementsToRemove)
                }
            }
        },
        [nodes, edges, onElementsRemove]
    )

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [onKeyDown])

    const onNodesChange = useCallback(
        (changes: any) =>
            setNodes((nodesSnapshot) =>
                applyNodeChanges(changes, nodesSnapshot)
            ),
        []
    )

    const onEdgesChange = useCallback(
        (changes: any) =>
            setEdges((edgesSnapshot) =>
                applyEdgeChanges(changes, edgesSnapshot)
            ),
        []
    )

    const onConnect = useCallback((params: any) => {
        setEdges((edgesSnapshot) => addEdge(params, edgesSnapshot))

        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === params.source) {
                    const currentQuestion = node.data.question as Question
                    const currentFollowed = currentQuestion?.followed || []

                    const newFollowed = currentFollowed.includes(params.target)
                        ? currentFollowed
                        : [...currentFollowed, params.target]

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            question: {
                                ...currentQuestion,
                                followed: newFollowed,
                            },
                        },
                    }
                }
                return node
            })
        )
    }, [])

    const onNodeClick = useCallback(
        (_event: any, node: Node) => {
            setSelectedNodeForModal(node)
            const question = node.data.question as Question

            const normalizedDependsOn = Array.isArray(question?.depends_on)
                ? question.depends_on
                : Array.isArray(question?.originalData?.depends_on)
                  ? (question.originalData?.depends_on || [])
                        .map((dependency) =>
                            typeof dependency === 'string'
                                ? dependency
                                : (dependency as any)?.question_id
                        )
                        .filter((dependencyId): dependencyId is string =>
                            Boolean(dependencyId)
                        )
                  : []

            setNodeTitle(question?.titulo || (node.data.rawLabel as string))
            setQuestionType(question?.tipo || 'text')
            setQuestionOptional(Boolean(question?.optional))
            const nodeFollowed = question?.followed || []
            setFollowedNodes([...nodeFollowed])

            setQuestionContent(question?.prompt || '')
            setAutonomousInstruction('')
            setQuestionTooltip(
                question?.originalData?.tooltip ?? question?.tooltip ?? ''
            )
            setOptionsDict({})
            setDependsOnNodes(Array.from(new Set(normalizedDependsOn)))

            if (question?.tipo === 'text' || question?.tipo === 'autonomous' || question?.tipo === 'document_upload') {
                setQuestionContent(question?.prompt || '')
            } else if (
                question?.tipo === 'select' ||
                question?.tipo === 'multiSelect' ||
                question?.tipo === 'table' ||
                question?.tipo === 'radio' ||
                question?.tipo === 'checkbox'
            ) {
                let existingOptions = question.conteudo as Record<
                    string,
                    string
                >

                if (
                    question?.tipo === 'table' &&
                    (!existingOptions || typeof existingOptions !== 'object')
                ) {
                    const headers =
                        question?.originalData?.type?.table?.headers || []
                    existingOptions = headers.reduce(
                        (acc, header) => {
                            acc[header.label] = header.name
                            return acc
                        },
                        {} as Record<string, string>
                    )
                }

                setOptionsDict(existingOptions || {})
            }

            if (question?.tipo === 'autonomous' || question?.tipo === 'document_upload') {
                setAutonomousInstruction(
                    question?.originalData?.instruction ??
                        question?.instruction ??
                        ''
                )
            }

            const originalQuestionNode = (
                contextFlowData?.questions || []
            ).find(
                (questionNode: any) =>
                    (questionNode?.question_data?.id || questionNode?.id) ===
                    node.id
            )

            const followingFromApi = originalQuestionNode?.following_questions
            const followingKeysFromApi: string[] =
                followingFromApi &&
                typeof followingFromApi === 'object' &&
                !Array.isArray(followingFromApi)
                    ? Object.entries(followingFromApi)
                          .filter(([, value]) => {
                              if (typeof value === 'string') {
                                  return true
                              }

                              return Boolean(
                                  value &&
                                  typeof value === 'object' &&
                                  typeof (value as any).question_id === 'string'
                              )
                          })
                          .map(([key]) => key)
                    : []

            const optionValues =
                question?.conteudo && typeof question.conteudo === 'object'
                    ? Object.values(question.conteudo)
                          .map((value) => String(value).trim())
                          .filter((value) => value !== '')
                    : []

            const savedFollowingKeys = question?.following_question_keys || []
            const initialFollowingKeys: string[] = []

            nodeFollowed.forEach((targetId, index) => {
                const savedKey = (savedFollowingKeys[index] || '').trim()
                const apiKey = (followingKeysFromApi[index] || '').trim()
                const optionKey = optionValues[index] || ''

                initialFollowingKeys[index] =
                    savedKey ||
                    apiKey ||
                    (isOptionBasedQuestionType(question?.tipo || 'text')
                        ? optionKey
                        : getDefaultFollowingQuestionKey(index))
            })

            setFollowedNodeKeys(initialFollowingKeys)

            setShowModal(true)
        },
        [contextFlowData]
    )

    const requestEditNode = useCallback(
        (nodeId: string) => {
            const node = nodes.find((n) => n.id === nodeId)
            if (node) {
                onNodeClick(null, node)
            }
        },
        [nodes, onNodeClick]
    )

    const requestDeleteNode = useCallback(
        (nodeId: string) => {
            const node = nodes.find((n) => n.id === nodeId)
            if (node) {
                setSelectedNodeForModal(node)
                setShowDeleteQuestionConfirm(true)
            }
        },
        [nodes]
    )

    const getViewportCenterFlowPosition = useCallback(() => {
        const container = flowWrapperRef.current
        const viewportElement = container?.querySelector(
            '.react-flow__viewport'
        ) as HTMLElement | null

        const jitter = () => (Math.random() - 0.5) * 60

        if (!container || !viewportElement) {
            return {x: Math.random() * 300, y: Math.random() * 300}
        }

        const match = viewportElement.style.transform.match(
            /translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([-\d.]+)\)/
        )

        const tx = match ? parseFloat(match[1]) : 0
        const ty = match ? parseFloat(match[2]) : 0
        const scale = match ? parseFloat(match[3]) || 1 : 1

        const rect = container.getBoundingClientRect()

        return {
            x: (rect.width / 2 - tx) / scale + jitter(),
            y: (rect.height / 2 - ty) / scale + jitter(),
        }
    }, [])

    const requestCloneNode = useCallback(
        (nodeId: string) => {
            const sourceNode = nodes.find((n) => n.id === nodeId)
            if (!sourceNode) return

            const sourceQuestion = sourceNode.data.question as Question
            const newNodeId = uuidv4()
            const clonedTitle = `${sourceQuestion?.titulo || 'pergunta'} (cópia)`
            const clonedQuestion: Question = {
                ...sourceQuestion,
                id: newNodeId,
                titulo: clonedTitle,
                followed: [],
                depends_on: [],
                following_question_keys: [],
                created_at: new Date().toISOString(),
                originalData: undefined,
            }

            const position = getViewportCenterFlowPosition()

            const newNode: Node = {
                id: newNodeId,
                position,
                data: {
                    label: buildNodeLabel(clonedTitle, clonedQuestion.tipo),
                    rawLabel: clonedTitle,
                    question: clonedQuestion,
                    isHead: false,
                },
                type: 'questionNode',
                style: getNodeStyle(clonedQuestion.tipo),
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
            }

            setNodes((nds) => [...nds, newNode])
            showFeedback('Pergunta clonada.', 'success')
        },
        [nodes, getViewportCenterFlowPosition, showFeedback]
    )

    const nodeActions = useMemo(
        () => ({
            requestEdit: requestEditNode,
            requestDelete: requestDeleteNode,
            requestClone: requestCloneNode,
        }),
        [requestEditNode, requestDeleteNode, requestCloneNode]
    )

    const nodeTypes = useMemo(() => ({questionNode: FlowQuestionNode}), [])

    const addNewNode = useCallback(() => {
        const newNodeId = uuidv4()
        const newQuestion: Question = {
            id: newNodeId,
            titulo: `pergunta_${nodes.length + 1}`,
            prompt: `Pergunta ${nodes.length + 1}`,
            tipo: 'text',
            conteudo: '',
            followed: [],
            depends_on: [],
            created_at: new Date().toISOString(),
            instruction: '',
            tooltip: '',
            optional: false,
        }

        const newNode = {
            id: newNodeId,
            position: getViewportCenterFlowPosition(),
            data: {
                label: buildNodeLabel(newQuestion.titulo, newQuestion.tipo),
                rawLabel: newQuestion.titulo,
                question: newQuestion,
                isHead: false,
            },
            type: 'questionNode',
            style: getNodeStyle(newQuestion.tipo),
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        }

        setNodeTitle(newQuestion.titulo)
        setQuestionType(newQuestion.tipo)
        setQuestionContent(newQuestion.prompt)
        setQuestionOptional(false)
        setAutonomousInstruction('')
        setQuestionTooltip('')
        setOptionsDict({})
        setFollowedNodes([])
        setDependsOnNodes([])
        setFollowedNodeKeys([])
        setSelectedNodeForModal(newNode as Node)
        setShowModal(true)
    }, [nodes.length, getViewportCenterFlowPosition])

    const handleSaveQuestion = useCallback(() => {
        if (!selectedNodeForModal) return

        const currentQuestion = selectedNodeForModal.data.question as
            | Question
            | undefined

        let content: string | Record<string, string>

        if (questionType === 'text') {
            content = questionContent
        } else if (
            questionType === 'select' ||
            questionType === 'multiSelect' ||
            questionType === 'table' ||
            questionType === 'radio' ||
            questionType === 'checkbox'
        ) {
            const optionBasedType = isOptionBasedQuestionType(questionType)

            if (optionBasedType) {
                const entries = Object.entries(optionsDict)

                if (entries.length === 0) {
                    showFeedback(
                        'Adicione ao menos uma opção com valor preenchido para salvar a pergunta.',
                        'error'
                    )
                    return
                }

                const hasEmptyLabel = entries.some(
                    ([label]) => label.trim() === ''
                )
                const hasEmptyValue = entries.some(
                    ([, value]) => value.trim() === ''
                )

                if (hasEmptyLabel || hasEmptyValue) {
                    showFeedback(
                        'Preencha todos os campos de opção (texto e valor) para salvar a pergunta.',
                        'error'
                    )
                    return
                }

                const normalizedOptionValues = entries.map(([, value]) =>
                    value.trim()
                )
                const duplicatedOptionValue = normalizedOptionValues.find(
                    (value, index) =>
                        normalizedOptionValues.indexOf(value) !== index
                )

                if (duplicatedOptionValue) {
                    showFeedback(
                        'Os valores enviados das opções devem ser únicos.',
                        'error'
                    )
                    return
                }
            }

            const filteredOptions: Record<string, string> = {}
            Object.entries(optionsDict).forEach(([key, value]) => {
                if (key.trim() !== '' && value.trim() !== '') {
                    filteredOptions[key] = value
                }
            })
            content = filteredOptions
        } else {
            content = questionContent
        }

        const optionValues =
            questionType !== 'text' &&
            questionType !== 'autonomous' &&
            content &&
            typeof content === 'object'
                ? Object.values(content)
                      .map((value) => String(value).trim())
                      .filter((value) => value !== '')
                : []

        const resolvedFollowingKeys: string[] = []
        const usedFollowingKeys = new Set<string>()

        for (let index = 0; index < followedNodes.length; index += 1) {
            const customKey = (followedNodeKeys[index] || '').trim()
            const fallbackKey = isOptionBasedQuestionType(questionType)
                ? optionValues[index] || ''
                : getDefaultFollowingQuestionKey(index)
            const resolvedKey = customKey || fallbackKey

            if (resolvedKey === '') {
                showFeedback(
                    'Preencha a chave de todas as ligações em Próximos Nós para salvar a pergunta.',
                    'error'
                )
                return
            }

            if (usedFollowingKeys.has(resolvedKey)) {
                showFeedback(
                    'As chaves de Próximos Nós devem ser únicas.',
                    'error'
                )
                return
            }

            usedFollowingKeys.add(resolvedKey)
            resolvedFollowingKeys[index] = resolvedKey
        }

        const updatedQuestion: Question = {
            id: selectedNodeForModal.id,
            titulo: nodeTitle,
            prompt: questionContent,
            tipo: questionType,
            conteudo: content,
            followed: followedNodes,
            depends_on: questionType === 'autonomous' ? dependsOnNodes : [],
            following_question_keys: resolvedFollowingKeys,
            created_at: currentQuestion?.created_at || new Date().toISOString(),
            instruction:
                questionType === 'autonomous' || questionType === 'document_upload'
                    ? autonomousInstruction
                    : undefined,
            tooltip: questionTooltip || null,
            optional: questionOptional,
        }

        // Verificar se é um novo nó (não existe na lista ainda)
        const isNewNode = !nodes.some((n) => n.id === selectedNodeForModal.id)

        if (isNewNode) {
            // Adicionar novo nó
            const newNode: Node = {
                ...selectedNodeForModal,
                data: {
                    ...selectedNodeForModal.data,
                    label: buildNodeLabel(nodeTitle, questionType),
                    rawLabel: nodeTitle,
                    question: updatedQuestion,
                },
                style: getNodeStyle(questionType),
            }
            setNodes((nds) => [...nds, newNode])
        } else {
            // Atualizar nó existente
            setNodes((nds) =>
                nds.map((node) => {
                    if (node.id === selectedNodeForModal.id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                label: buildNodeLabel(nodeTitle, questionType),
                                rawLabel: nodeTitle,
                                question: updatedQuestion,
                            },
                            style: getNodeStyle(questionType),
                        }
                    }
                    return node
                })
            )
        }

        setShowModal(false)
    }, [
        selectedNodeForModal,
        nodeTitle,
        questionType,
        questionContent,
        questionOptional,
        optionsDict,
        followedNodes,
        dependsOnNodes,
        followedNodeKeys,
        autonomousInstruction,
        questionTooltip,
        nodes,
        showFeedback,
    ])

    const handleDeleteQuestion = useCallback(() => {
        if (!selectedNodeForModal) return

        const nodeId = selectedNodeForModal.id
        setNodes((nds) => nds.filter((node) => node.id !== nodeId))
        setEdges((eds) =>
            eds.filter(
                (edge) => edge.source !== nodeId && edge.target !== nodeId
            )
        )

        setShowDeleteQuestionConfirm(false)
        setShowModal(false)
    }, [selectedNodeForModal])

    const addOption = useCallback(() => {
        setOptionsDict((prevOptions) => {
            const newKey = `Nova Opção ${Object.keys(prevOptions).length + 1}`
            return {
                ...prevOptions,
                [newKey]: '',
            }
        })
    }, [])

    const removeOption = useCallback((key: string) => {
        setOptionsDict((prevOptions) => {
            const newDict = {...prevOptions}
            delete newDict[key]
            return newDict
        })
    }, [])

    const handleOptionDragStart = useCallback((key: string) => {
        setDraggedOptionKey(key)
    }, [])

    const handleOptionDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>, key: string) => {
            event.preventDefault()
            if (dragOverOptionKey !== key) {
                setDragOverOptionKey(key)
            }
        },
        [dragOverOptionKey]
    )

    const handleOptionDrop = useCallback(
        (targetKey: string) => {
            setOptionsDict((prevOptions) => {
                if (!draggedOptionKey || draggedOptionKey === targetKey) {
                    return prevOptions
                }

                const entries = Object.entries(prevOptions)
                const fromIndex = entries.findIndex(
                    ([key]) => key === draggedOptionKey
                )
                const toIndex = entries.findIndex(([key]) => key === targetKey)

                if (fromIndex === -1 || toIndex === -1) {
                    return prevOptions
                }

                const reordered = [...entries]
                const [movedEntry] = reordered.splice(fromIndex, 1)
                reordered.splice(toIndex, 0, movedEntry)

                return Object.fromEntries(reordered)
            })

            setDraggedOptionKey(null)
            setDragOverOptionKey(null)
        },
        [draggedOptionKey]
    )

    const handleOptionDragEnd = useCallback(() => {
        setDraggedOptionKey(null)
        setDragOverOptionKey(null)
    }, [])

    const updateOptionKey = useCallback((oldKey: string, newKey: string) => {
        if (oldKey === newKey) return

        setOptionsDict((prevOptions) => {
            if (!Object.prototype.hasOwnProperty.call(prevOptions, oldKey)) {
                return prevOptions
            }

            if (
                newKey !== oldKey &&
                Object.prototype.hasOwnProperty.call(prevOptions, newKey)
            ) {
                return prevOptions
            }

            const entries = Object.entries(prevOptions)
            const renamedEntries = entries.map(([key, value]) =>
                key === oldKey ? [newKey, value] : [key, value]
            )

            return Object.fromEntries(renamedEntries)
        })
    }, [])

    const updateOptionValue = useCallback((key: string, value: string) => {
        setOptionsDict((prevOptions) => ({
            ...prevOptions,
            [key]: value,
        }))
    }, [])

    const generateValueFromLabel = useCallback((label: string): string => {
        return label
    }, [])

    const addFollowedNode = useCallback((nodeId: string) => {
        if (!nodeId) return

        setFollowedNodes((previous) => [...previous, nodeId])
        setFollowedNodeKeys((previous) => [...previous, ''])
    }, [])

    const removeFollowedNode = useCallback((indexToRemove: number) => {
        setFollowedNodes((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        )

        setFollowedNodeKeys((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        )
    }, [])

    const updateFollowedNodeKey = useCallback((index: number, key: string) => {
        setFollowedNodeKeys((previous) => {
            const updated = [...previous]
            updated[index] = key
            return updated
        })
    }, [])

    const addDependsOnNode = useCallback((nodeId: string) => {
        if (!nodeId) return

        setDependsOnNodes((previous) => {
            if (previous.includes(nodeId)) return previous
            return [...previous, nodeId]
        })
    }, [])

    const removeDependsOnNode = useCallback((nodeId: string) => {
        setDependsOnNodes((previous) => previous.filter((id) => id !== nodeId))
    }, [])

    useEffect(() => {
        setFollowedNodeKeys((previous) => {
            const optionValues = isOptionBasedQuestionType(questionType)
                ? Object.values(optionsDict)
                      .map((value) => String(value).trim())
                      .filter((value) => value !== '')
                : []

            const next = followedNodes.map((_, index) => {
                const currentValue = previous[index] || ''
                if (currentValue.trim() !== '') {
                    return currentValue
                }

                const fallbackValue = isOptionBasedQuestionType(questionType)
                    ? optionValues[index] || ''
                    : getDefaultFollowingQuestionKey(index)

                return fallbackValue
            })

            const changed =
                next.length !== previous.length ||
                next.some((value, index) => value !== previous[index])

            return changed ? next : previous
        })
    }, [followedNodes, questionType, optionsDict])

    const moveFollowedNodeUp = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index - 1], reordered[index]] = [
                reordered[index],
                reordered[index - 1],
            ]

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index - 1], reordered[index]] = [
                reordered[index],
                reordered[index - 1],
            ]

            return reordered
        })
    }, [])

    const moveFollowedNodeDown = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index < 0 || index >= previous.length - 1) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index], reordered[index + 1]] = [
                reordered[index + 1],
                reordered[index],
            ]

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index < 0 || index >= previous.length - 1) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index], reordered[index + 1]] = [
                reordered[index + 1],
                reordered[index],
            ]

            return reordered
        })
    }, [])

    const setFollowedNodeAsDefault = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            const [targetId] = reordered.splice(index, 1)
            reordered.unshift(targetId)

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            const [targetKey] = reordered.splice(index, 1)
            reordered.unshift(targetKey)

            return reordered
        })
    }, [])

    const modalFLowSelect = () => {
        const normalizedFilter = flowFilter.trim().toLowerCase()
        const filteredFlows = flows.filter((item) =>
            item.name.toLowerCase().includes(normalizedFilter)
        )

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowModalFlow(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '320px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Selecionar Fluxo</h2>
                    <div style={{marginTop: '12px'}}>
                        <input
                            type="text"
                            value={flowFilter}
                            onChange={(e) => setFlowFilter(e.target.value)}
                            placeholder="Filtrar fluxos..."
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                    <div
                        style={{
                            marginTop: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            maxHeight: '220px',
                            overflowY: 'auto',
                        }}
                    >
                        {filteredFlows.length === 0 && (
                            <div
                                style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    border: '1px dashed #d1d5db',
                                    borderRadius: '6px',
                                }}
                            >
                                Nenhum fluxo encontrado.
                            </div>
                        )}
                        {filteredFlows.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setFlowData(item.id)
                                    setShowModalFlow(false)
                                }}
                                style={{
                                    textAlign: 'left',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#f9fafb',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setShowModalFlow(false)}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Fechar
                    </button>
                </div>
            </div>
        )
    }

    const handleCreateFlow = useCallback(async () => {
        const trimmedName = newFlowName.trim()
        if (!trimmedName || isCreatingFlow) {
            return
        }

        setIsCreatingFlow(true)

        try {
            await flowApi.createFlow({
                document_flow: {
                    id: uuidv4(),
                    name: trimmedName,
                },
            })

            const refreshedFlows = await flowApi.getFlow()
            setFlows(refreshedFlows || [])

            setShowCreateFlowModal(false)
            setNewFlowName('')
            showFeedback('Fluxo criado com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao criar novo fluxo:', error)
            showFeedback('Falha ao criar o fluxo.', 'error')
            navigate('/error')
        } finally {
            setIsCreatingFlow(false)
        }
    }, [newFlowName, isCreatingFlow, flowApi, setFlows, showFeedback])

    const handleCloneFlow = useCallback(async () => {
        const trimmedName = cloneFlowName.trim()
        if (!trimmedName || !selectedFlowToCloneId || isCreatingFlow) {
            return
        }

        setIsCreatingFlow(true)

        try {
            const flowToClone = await flowApi.getFlowById(selectedFlowToCloneId)
            const {errors: _errors, ...flowToCloneWithoutErrors} = flowToClone

            await flowApi.createFlow({
                document_flow: {
                    ...flowToCloneWithoutErrors,
                    id: uuidv4(),
                    name: trimmedName,
                },
            })

            const refreshedFlows = await flowApi.getFlow()
            setFlows(refreshedFlows || [])

            setShowCloneFlowModal(false)
            setCloneFlowName('')
            setCloneFlowFilter('')
            setSelectedFlowToCloneId('')
            showFeedback('Fluxo clonado com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao clonar fluxo:', error)
            showFeedback('Falha ao clonar o fluxo.', 'error')
            navigate('/error')
        } finally {
            setIsCreatingFlow(false)
        }
    }, [
        cloneFlowName,
        selectedFlowToCloneId,
        isCreatingFlow,
        flowApi,
        setFlows,
        showFeedback,
    ])

    const modalCreateFlow = () => {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowCreateFlowModal(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '360px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Criar novo Fluxo</h2>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Nome do fluxo
                        </label>
                        <input
                            type="text"
                            value={newFlowName}
                            onChange={(e) => setNewFlowName(e.target.value)}
                            placeholder="Digite o nome do fluxo"
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <button
                            onClick={() => setShowCreateFlowModal(false)}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCreateFlow}
                            disabled={!newFlowName.trim() || isCreatingFlow}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor:
                                    !newFlowName.trim() || isCreatingFlow
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    !newFlowName.trim() || isCreatingFlow
                                        ? 0.7
                                        : 1,
                            }}
                        >
                            {isCreatingFlow ? 'Criando...' : 'Criar'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const modalCloneFlow = () => {
        const normalizedFilter = cloneFlowFilter.trim().toLowerCase()
        const filteredFlows = flows.filter((item) =>
            item.name.toLowerCase().includes(normalizedFilter)
        )

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowCloneFlowModal(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '360px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Clonar Fluxo</h2>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Nome do novo fluxo
                        </label>
                        <input
                            type="text"
                            value={cloneFlowName}
                            onChange={(e) => setCloneFlowName(e.target.value)}
                            placeholder="Digite o nome do novo fluxo"
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Selecionar fluxo base
                        </label>
                        <input
                            type="text"
                            value={cloneFlowFilter}
                            onChange={(e) => setCloneFlowFilter(e.target.value)}
                            placeholder="Filtrar fluxos..."
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            maxHeight: '180px',
                            overflowY: 'auto',
                        }}
                    >
                        {filteredFlows.length === 0 && (
                            <div
                                style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    border: '1px dashed #d1d5db',
                                    borderRadius: '6px',
                                }}
                            >
                                Nenhum fluxo encontrado.
                            </div>
                        )}
                        {filteredFlows.map((item) => {
                            const isSelected = selectedFlowToCloneId === item.id

                            return (
                                <button
                                    key={item.id}
                                    onClick={() =>
                                        setSelectedFlowToCloneId(item.id)
                                    }
                                    style={{
                                        textAlign: 'left',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: isSelected
                                            ? '1px solid #2563eb'
                                            : '1px solid #e5e7eb',
                                        backgroundColor: isSelected
                                            ? '#eff6ff'
                                            : '#f9fafb',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    {item.name}
                                </button>
                            )
                        })}
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <button
                            onClick={() => setShowCloneFlowModal(false)}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCloneFlow}
                            disabled={
                                !cloneFlowName.trim() ||
                                !selectedFlowToCloneId ||
                                isCreatingFlow
                            }
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor:
                                    !cloneFlowName.trim() ||
                                    !selectedFlowToCloneId ||
                                    isCreatingFlow
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    !cloneFlowName.trim() ||
                                    !selectedFlowToCloneId ||
                                    isCreatingFlow
                                        ? 0.7
                                        : 1,
                            }}
                        >
                            {isCreatingFlow ? 'Clonando...' : 'Clonar'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const modalQuestion = () => {
        const availableNodes = nodes.filter(
            (node) => node.id !== selectedNodeForModal?.id
        )
        const selectedQuestionId = selectedNodeForModal?.id || '-'
        const selectedQuestionIndex = selectedNodeForModal
            ? nodes.findIndex((node) => node.id === selectedNodeForModal.id)
            : -1

        const isOptionBasedType = [
            'select',
            'multiSelect',
            'table',
            'radio',
            'checkbox',
        ].includes(questionType)

        return (
            <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(12, 31, 41, 0.42)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'stretch',
                    zIndex: 2000,
                }}
                onClick={() => setShowModal(false)}
            >
                <div
                    style={{
                        background: '#ffffff',
                        padding: '0',
                        width: '460px',
                        maxWidth: '94vw',
                        height: '100%',
                        maxHeight: '100%',
                        boxShadow: '0 20px 50px rgba(12, 31, 41, 0.14)',
                        fontFamily: 'Inter, Roboto, sans-serif',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        style={{
                            padding: '20px 24px 15px',
                            borderBottom: '1px solid #e6eaec',
                            flexShrink: 0,
                        }}
                    >
                        <h2
                            style={{
                                fontSize: '19px',
                                fontWeight: 600,
                                letterSpacing: '-0.02em',
                                color: '#0c1f29',
                                margin: 0,
                            }}
                        >
                            {selectedNodeForModal?.id?.startsWith('n')
                                ? 'Nova Pergunta'
                                : 'Editar Pergunta'}
                        </h2>
                    </div>

                    <div
                        style={{
                            padding: '22px 24px',
                            flex: 1,
                            overflowY: 'auto',
                        }}
                    >
                    <div
                        style={{
                            marginBottom: '17px',
                            padding: '10px 12px',
                            backgroundColor: '#f2f5f6',
                            border: '1px solid #e6eaec',
                            borderRadius: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            fontSize: '11.5px',
                            color: '#5d6b72',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        <span>ID da pergunta: {selectedQuestionId}</span>
                        <span>
                            Índice no array:{' '}
                            {selectedQuestionIndex >= 0
                                ? selectedQuestionIndex
                                : '-'}
                        </span>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '7px', fontSize: '12.5px', fontWeight: 600, color: '#33454e'}}>
                            Label da Variável
                        </label>
                        <input
                            type="text"
                            value={nodeTitle}
                            onChange={(e) => setNodeTitle(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '11px 13px',
                                boxSizing: 'border-box',
                                border: '1.5px solid #d6dde0',
                                borderRadius: '10px',
                                fontSize: '14px',
                                color: '#0c1f29',
                            }}
                        />
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '7px', fontSize: '12.5px', fontWeight: 600, color: '#33454e'}}>
                            Tooltip
                        </label>
                        <textarea
                            value={questionTooltip}
                            onChange={(e) => setQuestionTooltip(e.target.value)}
                            rows={2}
                            style={{
                                width: '100%',
                                padding: '11px 13px',
                                boxSizing: 'border-box',
                                border: '1.5px solid #d6dde0',
                                borderRadius: '10px',
                                fontSize: '14px',
                                color: '#0c1f29',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                            }}
                        />
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '7px', fontSize: '12.5px', fontWeight: 600, color: '#33454e'}}>
                            Tipo de Pergunta
                        </label>
                        <select
                            value={questionType}
                            onChange={(e) => {
                                const newType = e.target.value as any

                                if (newType === 'yes_no') {
                                    setQuestionType('select')
                                    setOptionsDict({Sim: 'sim', Não: 'nao'})
                                    setAutonomousInstruction('')
                                    setQuestionOptional(false)
                                    return
                                }

                                setQuestionType(newType)

                                if (
                                    [
                                        'select',
                                        'multiSelect',
                                        'table',
                                        'radio',
                                        'checkbox',
                                    ].includes(newType)
                                ) {
                                    setOptionsDict({})
                                }

                                if (newType !== 'autonomous') {
                                    setAutonomousInstruction('')
                                }

                                if (isBranchingQuestionType(newType)) {
                                    setQuestionOptional(false)
                                }
                            }}
                            style={{width: '100%', padding: '11px 13px', border: '1.5px solid #d6dde0', borderRadius: '10px', fontSize: '14px', color: '#0c1f29', backgroundColor: '#ffffff'}}
                        >
                            <option value="text">Texto</option>
                            <option value="select">Select</option>
                            <option value="yes_no">Sim/Não (atalho)</option>
                            <option value="multiSelect">MultiSelect</option>
                            <option value="radio">Radio</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="table">Tabela</option>
                            <option value="autonomous">Autônomo</option>
                            <option value="table_autonomous">
                                Tabela Autônoma
                            </option>
                            <option value="document_upload">
                                Upload de Documento
                            </option>
                        </select>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: '#33454e'}}>
                            <input
                                type="checkbox"
                                checked={questionOptional}
                                disabled={isBranchingQuestionType(questionType)}
                                onChange={(e) =>
                                    setQuestionOptional(e.target.checked)
                                }
                            />
                            Pergunta opcional
                        </label>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '7px', fontSize: '12.5px', fontWeight: 600, color: '#33454e'}}>
                            Título da Pergunta
                        </label>

                        <textarea
                            value={questionContent}
                            onChange={(e) => setQuestionContent(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '11px 13px',
                                minHeight: '80px',
                                boxSizing: 'border-box',
                                border: '1.5px solid #d6dde0',
                                borderRadius: '10px',
                                fontSize: '14px',
                                color: '#0c1f29',
                            }}
                            placeholder={
                                questionType === 'table'
                                    ? 'Definir estrutura da tabela...'
                                    : 'Digite o título/conteúdo da pergunta...'
                            }
                        />

                        {isOptionBasedType && (
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        marginTop: '20px',
                                        marginBottom: '15px',
                                        padding: '10px',
                                        backgroundColor: '#f2f5f6',
                                        borderRadius: '6px',
                                        border: '1px solid #e6eaec',
                                    }}
                                >
                                    <div>
                                        <label
                                            style={{
                                                fontWeight: 'bold',
                                                color: '#0c1f29',
                                                display: 'block',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Headers da Tabela'
                                                : 'Opções'}
                                        </label>
                                        <small
                                            style={{
                                                color: '#5d6b72',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Formato: "label" : "name" (enviado em type.table.headers)'
                                                : 'Formato: "Opção Exibida" : "valor_enviado"'}
                                        </small>
                                    </div>

                                    <button
                                        onClick={addOption}
                                        style={{
                                            padding: '8px 16px',
                                            width: '100%    ',
                                            marginTop: '12px',
                                            backgroundColor: 'transparent',
                                            color: '#3294EF',
                                            border: '1px solid #3294EF',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {questionType === 'table'
                                            ? 'Adicionar Header'
                                            : 'Adicionar Opção'}
                                    </button>
                                </div>

                                {Object.keys(optionsDict).length === 0 && (
                                    <div
                                        style={{
                                            padding: '30px',
                                            textAlign: 'center',
                                            color: '#5d6b72',
                                            fontStyle: 'italic',
                                            border: '2px dashed #e6eaec',
                                            borderRadius: '8px',
                                            backgroundColor: '#f7f9fa',
                                        }}
                                    >
                                        <p
                                            style={{
                                                margin: '0 0 10px 0',
                                                fontSize: '16px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Nenhum header adicionado'
                                                : 'Nenhuma opção adicionada'}
                                        </p>
                                        <p
                                            style={{
                                                margin: '0',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Clique em "+ Adicionar Header" para começar'
                                                : 'Clique em "+ Adicionar Opção" para começar'}
                                        </p>
                                    </div>
                                )}

                                {Object.entries(optionsDict).map(
                                    ([key, value], index) => (
                                        <div
                                            key={`option-row-${index}`}
                                            onDragOver={(event) =>
                                                handleOptionDragOver(event, key)
                                            }
                                            onDrop={() => handleOptionDrop(key)}
                                            style={{
                                                flexDirection: 'column',
                                                gap: '10px',
                                                marginBottom: '12px',
                                                alignItems: 'flex-start',
                                                padding: '15px',
                                                backgroundColor:
                                                    dragOverOptionKey === key
                                                        ? '#eaf3fd'
                                                        : '#f2f5f6',
                                                borderRadius: '8px',
                                                border:
                                                    draggedOptionKey === key
                                                        ? '1px solid #3294EF'
                                                        : '1px solid #e6eaec',
                                            }}
                                        >
                                            <div
                                                draggable
                                                onDragStart={() =>
                                                    handleOptionDragStart(key)
                                                }
                                                onDragEnd={handleOptionDragEnd}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    cursor: 'grab',
                                                    userSelect: 'none',
                                                }}
                                                title={
                                                    questionType === 'table'
                                                        ? 'Arraste para reordenar header'
                                                        : 'Arraste para reordenar opção'
                                                }
                                            >
                                                <small
                                                    style={{
                                                        color: '#5d6b72',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Arraste para reordenar
                                                </small>
                                                <span
                                                    style={{
                                                        color: '#5d6b72',
                                                        fontSize: '18px',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    ::
                                                </span>
                                            </div>

                                            <div>
                                                <label
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '6px',
                                                        color: '#33454e',
                                                    }}
                                                >
                                                    {questionType === 'table'
                                                        ? 'Label (enviado no payload):'
                                                        : 'Opção (exibida ao usuário):'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={key}
                                                    onChange={(e) => {
                                                        const newLabel =
                                                            e.target.value
                                                        const isValueAutoGenerated =
                                                            value === '' ||
                                                            value ===
                                                                generateValueFromLabel(
                                                                    key
                                                                )

                                                        updateOptionKey(
                                                            key,
                                                            newLabel
                                                        )

                                                        if (
                                                            isValueAutoGenerated
                                                        ) {
                                                            updateOptionValue(
                                                                newLabel,
                                                                generateValueFromLabel(
                                                                    newLabel
                                                                )
                                                            )
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d6dde0',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                    }}
                                                    placeholder={
                                                        questionType === 'table'
                                                            ? 'Ex: name'
                                                            : 'Ex: Primeira Opção'
                                                    }
                                                />
                                            </div>

                                            <div style={{flex: 1}}>
                                                <label
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '6px',
                                                        color: '#33454e',
                                                    }}
                                                >
                                                    {questionType === 'table'
                                                        ? 'Nome (exibido para o usuário):'
                                                        : 'Valor (enviado no formulário):'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) =>
                                                        updateOptionValue(
                                                            key,
                                                            e.target.value
                                                        )
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d6dde0',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                    }}
                                                    placeholder={
                                                        questionType === 'table'
                                                            ? 'Ex: Nome do Item'
                                                            : 'Ex: primeira_opcao'
                                                    }
                                                />
                                            </div>

                                            <button
                                                onClick={() =>
                                                    removeOption(key)
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#fdecec',
                                                    color: '#cf3434',
                                                    border: '1px solid #cf3434',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    marginTop: '24px',
                                                }}
                                                title="Remover opção"
                                            >
                                                {questionType === 'table'
                                                    ? 'Remover header'
                                                    : 'Remover opção'}
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {(questionType === 'autonomous' || questionType === 'document_upload') && (
                            <div style={{marginTop: '15px'}}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {questionType === 'document_upload'
                                        ? 'Instruções de extração do documento'
                                        : 'Instruções do modo autônomo'}
                                </label>
                                <textarea
                                    value={autonomousInstruction}
                                    onChange={(e) =>
                                        setAutonomousInstruction(e.target.value)
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '11px 13px',
                                        minHeight: '80px',
                                        boxSizing: 'border-box',
                                        border: '1.5px solid #d6dde0',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        color: '#0c1f29',
                                    }}
                                    placeholder={
                                        questionType === 'document_upload'
                                            ? 'Descreva o que deve ser extraído do documento enviado pelo usuário'
                                            : 'Descreva as instruções que serão usadas pelo assistente'
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {/* Próximos nós (múltiplos) */}
                    <div style={{marginBottom: '20px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#8a969c',
                                paddingBottom: '10px',
                                borderBottom: '1px solid #e6eaec',
                            }}
                        >
                            Próximos Nós (Followed)
                        </label>

                        {/* Lista dos nós já selecionados */}
                        {followedNodes.length > 0 && (
                            <div style={{marginBottom: '10px'}}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontSize: '12px',
                                        color: '#5d6b72',
                                    }}
                                >
                                    Nós conectados:
                                </label>
                                {followedNodes.map((nodeId, index) => {
                                    const node = nodes.find(
                                        (n) => n.id === nodeId
                                    )
                                    const priorityLabel =
                                        index === 0
                                            ? 'Default'
                                            : `Default ${index + 1}`
                                    const followedKey =
                                        followedNodeKeys[index] ||
                                        (isOptionBasedQuestionType(questionType)
                                            ? ''
                                            : priorityLabel)
                                    return (
                                        <div
                                            key={`${nodeId}-${index}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                backgroundColor: '#f2f5f6',
                                                padding: '8px 12px',
                                                borderRadius: '4px',
                                                marginBottom: '5px',
                                                border: '1px solid #e6eaec',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '4px',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        color: '#0e8f78',
                                                    }}
                                                >
                                                    Prioridade: {priorityLabel}
                                                </span>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            color: '#33454e',
                                                            minWidth: '52px',
                                                        }}
                                                    >
                                                        Chave:
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={followedKey}
                                                        onChange={(event) =>
                                                            updateFollowedNodeKey(
                                                                index,
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder={
                                                            isOptionBasedQuestionType(
                                                                questionType
                                                            )
                                                                ? 'Informe a chave da ligação'
                                                                : priorityLabel
                                                        }
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '220px',
                                                            padding: '4px 6px',
                                                            border: '1px solid #d6dde0',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    style={{fontSize: '14px'}}
                                                >
                                                    {(node?.data
                                                        .rawLabel as string) ||
                                                        nodeId}{' '}
                                                    ({nodeId})
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '6px',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <button
                                                    onClick={() =>
                                                        setFollowedNodeAsDefault(
                                                            index
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index === 0
                                                                ? '#5d6b72'
                                                                : '#0e8f78',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '11px',
                                                        cursor:
                                                            index === 0
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Definir como Default"
                                                >
                                                    Default
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        moveFollowedNodeUp(
                                                            index
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index === 0
                                                                ? '#aeb7bc'
                                                                : '#3294EF',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor:
                                                            index === 0
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Subir prioridade"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        moveFollowedNodeDown(
                                                            index
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        followedNodes.length - 1
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index ===
                                                            followedNodes.length -
                                                                1
                                                                ? '#aeb7bc'
                                                                : '#3294EF',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor:
                                                            index ===
                                                            followedNodes.length -
                                                                1
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Descer prioridade"
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        removeFollowedNode(
                                                            index
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            '#cf3434',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {/* Seletor para adicionar novos nós */}
                        <div style={{display: 'flex', gap: '10px'}}>
                            <select
                                style={{
                                    flex: 1,
                                    padding: '8px',
                                    border: '1px solid #e6eaec',
                                    borderRadius: '4px',
                                }}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        addFollowedNode(e.target.value)
                                        e.target.value = ''
                                    }
                                }}
                                defaultValue=""
                            >
                                <option value="">
                                    Selecione um nó para conectar...
                                </option>
                                {availableNodes.map((node) => (
                                    <option key={node.id} value={node.id}>
                                        {node.data.rawLabel as string} (
                                        {node.id})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <small style={{color: '#5d6b72', fontSize: '12px'}}>
                            A chave de cada ligação será usada no
                            following_questions. Em perguntas de múltipla
                            escolha, o recomendado é usar o valor da opção.
                        </small>
                    </div>

                    {questionType === 'autonomous' && (
                        <div style={{marginBottom: '20px'}}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '12px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.07em',
                                    color: '#8a969c',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid #e6eaec',
                                }}
                            >
                                Dependências (depends_on)
                            </label>

                            {dependsOnNodes.length > 0 && (
                                <div style={{marginBottom: '10px'}}>
                                    {dependsOnNodes.map((nodeId) => {
                                        const node = nodes.find(
                                            (candidate) =>
                                                candidate.id === nodeId
                                        )

                                        return (
                                            <div
                                                key={`depends-on-${nodeId}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    backgroundColor: '#f2f5f6',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                    marginBottom: '5px',
                                                    border: '1px solid #e6eaec',
                                                }}
                                            >
                                                <span
                                                    style={{fontSize: '14px'}}
                                                >
                                                    {(node?.data
                                                        .rawLabel as string) ||
                                                        nodeId}{' '}
                                                    ({nodeId})
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        removeDependsOnNode(
                                                            nodeId
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            '#cf3434',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            <div style={{display: 'flex', gap: '10px'}}>
                                <select
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #e6eaec',
                                        borderRadius: '4px',
                                    }}
                                    onChange={(event) => {
                                        if (event.target.value) {
                                            addDependsOnNode(event.target.value)
                                            event.target.value = ''
                                        }
                                    }}
                                    defaultValue=""
                                >
                                    <option value="">
                                        Selecione uma questão para
                                        dependência...
                                    </option>
                                    {availableNodes
                                        .filter(
                                            (node) =>
                                                !dependsOnNodes.includes(
                                                    node.id
                                                )
                                        )
                                        .map((node) => (
                                            <option
                                                key={`depends-select-${node.id}`}
                                                value={node.id}
                                            >
                                                {node.data.rawLabel as string} (
                                                {node.id})
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <small style={{color: '#5d6b72', fontSize: '12px'}}>
                                Os IDs selecionados serão salvos em depends_on e
                                não criam ligação visual.
                            </small>
                        </div>
                    )}
                    </div>

                    {/* Botões */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '15px 24px',
                            borderTop: '1px solid #e6eaec',
                            flexShrink: 0,
                        }}
                    >
                        <button
                            onClick={() => setShowDeleteQuestionConfirm(true)}
                            style={{
                                padding: '9px 14px',
                                backgroundColor: '#ffffff',
                                color: '#cf3434',
                                border: '1px solid #f5c9c9',
                                borderRadius: '9px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                            }}
                        >
                            Excluir
                        </button>
                        <div style={{display: 'flex', gap: '9px'}}>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    padding: '9px 14px',
                                    backgroundColor: '#ffffff',
                                    color: '#33454e',
                                    border: '1px solid #d6dde0',
                                    borderRadius: '9px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveQuestion}
                                style={{
                                    padding: '9px 14px',
                                    backgroundColor: '#3294EF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '9px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </>
        )
    }

    if (isLoading) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div>Carregando flow...</div>
            </div>
        )
    }

    const topbarStyle: React.CSSProperties = {
        height: '60px',
        flexShrink: 0,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e6eaec',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 18px',
        gap: '14px',
        fontFamily: 'Inter, Roboto, sans-serif',
    }

    const flowPillStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        backgroundColor: '#f2f5f6',
        border: '1px solid #e6eaec',
        padding: '7px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#0c1f29',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    }

    const btnGroupStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
    }

    const outlinedBtn: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '8px 13px',
        backgroundColor: '#ffffff',
        color: '#33454e',
        border: '1px solid #d6dde0',
        borderRadius: '9px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
    }

    const primaryBtn: React.CSSProperties = {
        ...outlinedBtn,
        backgroundColor: '#3294EF',
        color: '#ffffff',
        border: 'none',
    }

    const divider: React.CSSProperties = {
        width: '1px',
        height: '24px',
        backgroundColor: '#e6eaec',
        margin: '0 3px',
        flexShrink: 0,
    }

    const iconSx = {fontSize: 15}

    return (
        <div
            style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={topbarStyle}>
                <button
                    onClick={() => navigate('/adminPage')}
                    style={outlinedBtn}
                >
                    <KeyboardBackspaceIcon sx={iconSx} />
                    Voltar para o Menu Admin
                </button>

                <div style={divider} />

                <button
                    onClick={() => setShowModalFlow(true)}
                    style={flowPillStyle}
                >
                    <AccountTreeOutlinedIcon
                        sx={{...iconSx, color: '#3294EF'}}
                    />
                    {contextFlowData?.name || 'Selecionar fluxo'}
                    <KeyboardArrowDownIcon sx={{...iconSx, color: '#8a969c'}} />
                </button>

                <div style={btnGroupStyle}>
                    <button
                        onClick={() =>
                            navigate('/upload-documents', {
                                state: {flow: contextFlowData},
                            })
                        }
                        style={outlinedBtn}
                    >
                        <DescriptionOutlinedIcon sx={iconSx} />
                        Editar documentos
                    </button>

                    <div style={divider} />

                    <button
                        onClick={() => setShowCreateFlowModal(true)}
                        style={outlinedBtn}
                    >
                        <AddOutlinedIcon sx={iconSx} />
                        Novo fluxo
                    </button>
                    <button
                        onClick={() => setShowCloneFlowModal(true)}
                        style={outlinedBtn}
                    >
                        <ContentCopyOutlinedIcon sx={iconSx} />
                        Clonar
                    </button>

                    <div style={divider} />

                    <button onClick={addNewNode} style={outlinedBtn}>
                        <AddOutlinedIcon sx={iconSx} />
                        Nova pergunta
                    </button>
                    <button onClick={handleDownloadFlowImage} style={outlinedBtn}>
                        <ImageOutlinedIcon sx={iconSx} />
                        Baixar imagem
                    </button>

                    <div style={divider} />

                    <button onClick={handleUpdateFlow} style={primaryBtn}>
                        <SaveOutlinedIcon sx={iconSx} />
                        Salvar fluxo
                    </button>
                </div>
            </div>

            <div
                ref={flowWrapperRef}
                className="flow-question-canvas"
                style={{flex: 1, minHeight: 0, width: '100%'}}
            >
                <NodeActionsContext.Provider value={nodeActions}>
                    <EdgeActionsContext.Provider value={edgeActions}>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            fitView
                            deleteKeyCode={['Delete']}
                            defaultEdgeOptions={{
                                type: 'deletable',
                                style: {stroke: 'black', strokeWidth: 1.5},
                                markerEnd: {
                                    type: MarkerType.ArrowClosed,
                                    width: 12,
                                    height: 12,
                                    color: 'black',
                                },
                            }}
                        >
                            <Background bgColor="#2f99e04d" />
                            <Panel
                                className="flex gap-1 rounded-md bg-primary-foreground p-1 text-foreground"
                                position="top-right"
                            >
                                <NodeSearch />
                            </Panel>
                            <Controls />
                        </ReactFlow>
                    </EdgeActionsContext.Provider>
                </NodeActionsContext.Provider>
            </div>
            {showModal && modalQuestion()}
            {showModalFlow && modalFLowSelect()}
            {showCreateFlowModal && modalCreateFlow()}
            {showCloneFlowModal && modalCloneFlow()}
            {flowValidationErrors.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2100,
                    }}
                >
                    <div
                        style={{
                            background: 'white',
                            padding: '24px',
                            width: '480px',
                            maxWidth: '90vw',
                            borderRadius: '8px',
                            border: '1px solid #f5c2c7',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                        }}
                    >
                        <h2 style={{color: '#b91c1c', margin: 0}}>
                            Erro ao salvar o fluxo
                        </h2>
                        <div
                            style={{
                                marginTop: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                maxHeight: '320px',
                                overflowY: 'auto',
                            }}
                        >
                            {flowValidationErrors.map((error, index) => (
                                <div
                                    key={index}
                                    style={{
                                        padding: '12px',
                                        borderRadius: '6px',
                                        backgroundColor: '#fde8e8',
                                        border: '1px solid #f5c2c7',
                                        color: '#7f1d1d',
                                        fontSize: '14px',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {error}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => setFlowValidationErrors([])}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}
            {showDeleteQuestionConfirm && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2100,
                    }}
                    onClick={() => setShowDeleteQuestionConfirm(false)}
                >
                    <div
                        style={{
                            background: 'white',
                            padding: '24px',
                            width: '360px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Excluir pergunta</h2>
                        <p style={{fontSize: '14px', color: '#333'}}>
                            Tem certeza que deseja excluir esta pergunta? Essa
                            ação não pode ser desfeita e removerá também as
                            ligações relacionadas a ela.
                        </p>
                        <div
                            style={{
                                marginTop: '16px',
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <button
                                onClick={() =>
                                    setShowDeleteQuestionConfirm(false)
                                }
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteQuestion}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {edgeToDelete && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2100,
                    }}
                    onClick={cancelDeleteEdge}
                >
                    <div
                        style={{
                            background: 'white',
                            padding: '24px',
                            width: '360px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Remover ligação</h2>
                        <p style={{fontSize: '14px', color: '#333'}}>
                            Tem certeza que deseja remover esta ligação entre
                            as perguntas? Essa ação não pode ser desfeita.
                        </p>
                        <div
                            style={{
                                marginTop: '16px',
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <button
                                onClick={cancelDeleteEdge}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmDeleteEdge}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Remover
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {edgeToEdit && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2100,
                    }}
                    onClick={cancelEditEdge}
                >
                    <div
                        style={{
                            background: 'white',
                            padding: '24px',
                            width: '360px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Editar ligação</h2>
                        <div style={{marginTop: '12px'}}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '14px',
                                }}
                            >
                                Chave da ligação
                            </label>
                            <input
                                type="text"
                                value={edgeEditKeyDraft}
                                onChange={(e) =>
                                    setEdgeEditKeyDraft(e.target.value)
                                }
                                placeholder="Informe a chave da ligação"
                                autoFocus
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div
                            style={{
                                marginTop: '16px',
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <button
                                onClick={cancelEditEdge}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmEditEdge}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    )
}
